import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ChromePicker } from 'react-color';
import { ColorPaletteEntry } from './parser/propTypes';
import { colorResultToVec3, PropComponentProps, PropLayout, vec3ToHTMLColor } from './propComponents';
import { colorPickerStyles } from './styles/colorPickerStyles';

// The $prop color_palette editor

// Gradient Display draws a gradient using an svg + the linearGradient component
interface GradientDisplayProps {
  gradient: ColorPaletteEntry[];
}
export const makeHTMLId = () => '' + Math.random().toString(36).substr(2, 9);
function GradientDisplay(props: GradientDisplayProps) {
  const gradientId = useMemo(makeHTMLId, [props.gradient.length]);
  return (
    <div className="gradientContainer">
      <svg width="100%" height="100%">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0.5" x2="1" y2="0.5">
            {
              props.gradient.map((entry, index) => 
                <stop 
                  key={index} 
                  offset={entry.offset} 
                  style={{stopColor: vec3ToHTMLColor(entry.color), stopOpacity: 1.0}}
                />
              )
            }
          </linearGradient>
        </defs>
        <rect x="0" y ="0" width="100%" height="100%" fill={`url(#${gradientId})`}/>
      </svg>
    </div>
  );
}

// A single color entry on the gradient display
interface ColorStopProps {
  entry: ColorPaletteEntry;
  active: boolean;
  onMouseDown: React.MouseEventHandler;
  onRemove: React.MouseEventHandler;
}

function ColorStop(props: ColorStopProps) {
  const { entry, active, onMouseDown, onRemove } = props;
  return (
    <div 
      className="colorStop"
      style={{left: `calc(${entry.offset * 100}% - 6px)`}}
      onMouseDown={onMouseDown}
    >
      <div 
        className={`colorStopColor ${active ? "colorStopColorActive" : ""}`}
        style={{backgroundColor: vec3ToHTMLColor(entry.color)}}
      />
      <div
        className="colorStopRemove"
        onMouseDown={onRemove}
      >X</div>
    </div>
  )
}

// insert an element into a ColorPaletteEntry[], and return the new sorted array + the element's index
function insertEntry(entries: ColorPaletteEntry[], entry: ColorPaletteEntry): [ColorPaletteEntry[], number] {
  let res = [];
  let entryIndex = 0;
  let pushed = false;
  for(let i = 0; i < entries.length; i++) {
    if(entry.offset < entries[i].offset && !pushed) {
      res.push(entry);
      entryIndex = i;
      pushed = true;
    }
    res.push(entries[i]);
  }
  if(entry.offset > entries[entries.length - 1].offset && !pushed) {
    res.push(entry);
    entryIndex = entries.length;
  }

  return [res, entryIndex];
}

// adjust an elements offset, and return a sorted ColorPaletteEntry[] + the element's new index
function adjustEntryOffset(entries: ColorPaletteEntry[], index: number, entryOffset: number): [ColorPaletteEntry[], number] {
  // remove entry 
  let newEntries = entries.filter((_, i) => i !== index);
  // construct new entry
  const newEntry = entries[index];
  newEntry.offset = entryOffset;

  // add entry at front if necessary
  if(newEntries.length > 0 && entryOffset <= newEntries[0].offset) {
    newEntries.splice(0, 0, newEntry);
    return [newEntries, 0];
  }
  // add entry in middle if necessary
  for(let i = 0; i < newEntries.length - 1; i++) {
    if(entryOffset > newEntries[i].offset && entryOffset <= newEntries[i + 1].offset) {
      newEntries.splice(i + 1, 0, newEntry);
      return [newEntries, i + 1];
    }
  }
  // add entry at end if necessary
  newEntries.push(newEntry);
  return [newEntries, newEntries.length - 1];
}

// Container with multiple color stops
interface ColorStopsEditorProps {
  entries: ColorPaletteEntry[];
  setEntries: (entries: ColorPaletteEntry[]) => void;
}
function ColorStopsEditor(props: ColorStopsEditorProps) {
  const {entries, setEntries} = props;
  // containing div
  const container = useRef<HTMLDivElement | null>(null);
  // index of color stop currently being dragged
  const [activeDrag, setActiveDrag] = useState<number | null>(null);
  // index of color whose color is being adjusted
  const [selectedColor, setSelectedColor] = useState<number | null>(null);

  // add a new color stop
  const handleAdd = useCallback((event: React.MouseEvent) => {
    if(container.current === null) return;

    // get mouse offset in container
    const containerPos = container.current.getBoundingClientRect();
    const offset = (event.clientX - containerPos.left) / containerPos.width;
    // add entry
    const newEntry: ColorPaletteEntry = { offset: offset, color: [0.5, 0.5, 0.5] };
    const [newEntries, index] = insertEntry(entries, newEntry);

    setEntries(newEntries);
    setActiveDrag(index);
    setSelectedColor(index);
  }, [entries, setEntries]);

  // select a color step
  const clickColorStop = useCallback((event: React.MouseEvent, index: number) => {
    setActiveDrag(index);
    setSelectedColor(index);
    event.preventDefault();
    event.stopPropagation();
  }, [setActiveDrag, setSelectedColor]);

  // drag a color stop's offset
  const mouseMove = useCallback((event: React.MouseEvent) => {
    if(activeDrag === null || container.current === null) return;

    // get mouse offset in container
    const containerPos = container.current.getBoundingClientRect();
    const offset = (event.clientX - containerPos.left) / containerPos.width;
    // restrict offset to [0.0, 1.0]
    const offsetClamped = Math.min(Math.max(offset, 0.0), 1.0);

    // set active stop's offset
    const [newEntries, index] = adjustEntryOffset(entries, activeDrag, offsetClamped);
    setEntries(newEntries);
    setActiveDrag(index);
    setSelectedColor(index);
  }, [activeDrag, entries, setEntries]);

  // end dragging a color stop
  const mouseUp = useCallback((event: React.MouseEvent) => {
    setActiveDrag(null);
  }, []);

  // remove a color stop
  const remove = useCallback((event: React.MouseEvent, index: number) => {
    if(entries.length <= 1) return;
    entries.splice(index, 1);
    setEntries(entries);
    setActiveDrag(null);
    // if we deleted the selected color, remove selection
    if(index === selectedColor) {
      setSelectedColor(null);
    }
    // if we deleted an color before selected, maintain selected index properly
    if(selectedColor !== null && index < selectedColor) {
      setSelectedColor(selectedColor - 1);
    }
    event.preventDefault();
    event.stopPropagation();
  }, [entries, setEntries, selectedColor]);

  return (
    <>
      <div 
        ref={container}
        className="colorStopContainer"
        onMouseDown={handleAdd}
        onMouseUp={mouseUp}
        onMouseLeave={mouseUp}
        onMouseMove={mouseMove}
      >
        {entries.map((entry, index) => 
          <ColorStop
            key={index}
            entry={entry} 
            active={selectedColor !== null && index === selectedColor}
            onMouseDown={(event) => clickColorStop(event, index)}
            onRemove={(event) => remove(event, index)}
          />
        )}
      </div>
      {selectedColor !== null &&
        <div className="flex colorPickerContainer">
          <ChromePicker
            color={vec3ToHTMLColor(entries[selectedColor].color)}
            onChange={(color) => {
              entries[selectedColor].color = colorResultToVec3(color);
              setEntries(entries);
            }}
            styles={colorPickerStyles}
          />
          <div
            className="gradientColorPickerClose"
            onClick={() => setSelectedColor(null)}>X</div>
        </div>
      }
    </>
  )
}

// Gradient property editor (for $prop color_palette properties)
export function ColorGradient(props: PropComponentProps) {
  const value = props.value.value as ColorPaletteEntry[];

  return (
    <PropLayout {...props}>
      <GradientDisplay gradient={value}></GradientDisplay>
      <div className="gradientMargins">
        <ColorStopsEditor entries={value} setEntries={(entires) => props.setValue({value: entires, forceConst: props.value.forceConst})} />
      </div>
    </PropLayout>
  );
}